/* -------------------------- Design imports start -------------------------- */
import { CssBaseline, ThemeProvider, useMediaQuery } from '@mui/material'
import { ConfigProvider } from 'antd'
import Cookies from 'js-cookie'
import { Suspense, lazy, useCallback, useEffect, useRef, useState, type ReactElement } from 'react'
import { unstable_batchedUpdates } from 'react-dom'
import { BrowserRouter, Redirect, Switch, useHistory } from 'react-router-dom'

import { AuthLayout, EmptyLayout, MainLayout } from './components/layout/MainLayout'
import { AuthenticatedRoute, PublicRoute } from './components/layout/Route'
import LoadingSkeleton from './components/widgets/LoadingSkeleton'
import ChangePasswordPage from './pages/Auth/ChangePasswordPage'
import GuestSignInPage from './pages/Auth/GuestSignInPage'
import SignInPage from './pages/Auth/SignInPage'
import SignUpPage from './pages/Auth/SignUpPage'
import PasswordResetPage from './pages/Auth/ResetPasswordPage'
import UnauthRequestPage from './pages/Auth/UnauthRequestPage'
import CheckEmailPage from './pages/Auth/CheckEmailPage'
import VerifyPage from './pages/Auth/VerifyPage'
import WrongRoutePage from './pages/Helper/WrongRoutePage'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */

import { ContractTabType, CountContext, UserContext, useUserContext } from './utils/context'
import { canView, isPrivileged, refreshAccessToken, rolesToPermissions } from './utils/functions'
import MUItheme from './utils/theme'
import { JoyrideProvider } from './components/widgets/Joyride'
import { type Role, type User } from './utils/types'
import { type NewPermissions } from './features/Roles/utils/types'
import LogTool from './logger/logTools'
import WebsocketProvider, { useWebsocket } from './utils/WebsocketProvider'
import UnauthWebsocketProvider from './utils/UnauthWebsocketProvider'
import { type GuestRequest } from './features/UnauthRequest/utils/types'
import { type Offer, type Request } from './features/Request/utils/types'
import { type Bill, type Contract } from './features/Contract/utils/types'
import SharepointGlobalStateProvider from './features/Sharepoint/SharepointGlobalStates'
import ChatSystemGlobalStateProvider from './features/ChatSystem/ChatSystemGlobalStateProvider'
import { isFeatureEnabled } from './config/featureFlags'
// React Query API imports
import { fetchCurrentUser } from './api/userApi'
import { fetchUnreadOverview } from './api/unreadOverview'
import { fetchOrganization } from './features/Organization/api/organizationApi'
import { PERMISSIONS } from './config/permissions'
import { type PaginatedQueryParameters } from './api/hooks/utils/types'
import { type GuestRequestFilters } from './features/UnauthRequest/hooks/useGuestRequestQueries'
import { type RequestFilters, type OfferFilters } from './features/Request/api/requestApi'
import { type ContractFilters, type BillFilters } from './features/Contract/api/contractApi'
import { defaultTablePageSize } from './config/ui'
/* ------------------------- Functional imports end ------------------------- */
const stockChangeDiagramPage = lazy(() => import('./features/Stock/StockChangeDiagramPage'))
const productionOrderPage = lazy(() => import('./pages/Order/ProductionOrderPage'))
const itemStockPage = lazy(() => import('./features/Stock/ItemStockPage'))
const ordersPage = lazy(() => import('./pages/Order/OrdersPage'))
const fileSystemPage = lazy(() => import('./pages/Filesystem/FileSystemPage'))
const chatSystemPage = lazy(() => import('./pages/Chat/ChatSystemPage'))
const bOMListPage = lazy(() => import('./pages/BOM/BOMListPage'))
const bOMDetailPage = lazy(() => import('./pages/BOM/BOMDetailPage'))
const profilePage = lazy(() => import('./pages/Profile/ProfilePage'))
const itemsPage = lazy(() => import('./pages/Article/itemsPage'))
const sharepointOverviewPage = lazy(() => import('./pages/Sharepoint/SharepointOverviewPage'))
const mainLayoutWithSetUser = lazy(() => import('./pages/Article/itemsPage'))
const customerPage = lazy(() => import('./pages/Customer/CustomerPage'))
const customerOrdersPage = lazy(() => import('./features/Customer/CustomerOrdersPage'))
const employeePage = lazy(() => import('./pages/Employee/EmployeePage'))
const organizationPage = lazy(() => import('./pages/Organization/OrganizationPage'))
const rolesPage = lazy(() => import('./pages/Roles/RolesPage'))
const supplierPage = lazy(() => import('./pages/Supplier/SupplierPage'))
const supportPage = lazy(() => import('./pages/Support/SupportPage'))
const contractOverviewAdminPage = lazy(() => import('./pages/Contract/ContractOverviewAdminPage'))
const contractAdminPage = lazy(() => import('./pages/Contract/ContractAdminPage'))
const contactPage = lazy(() => import('./pages/Contact/ContactPage'))
const contactGroupPage = lazy(() => import('./pages/Contact/ContactGroupPage'))
const contractSupplierSelectPage = lazy(() => import('./pages/Contract/ContractSupplierSelectPage'))
const guestRequestPage = lazy(() => import('./pages/AQGRequest/AQGRequestPage'))
const testGuestRequestPage = lazy(() => import('./pages/AQGRequest/TestAQGRequestPage'))
const cadUploadPage = lazy(() => import('./features/CADUpload/CADUploadPage'))
const cadUploadContentPage = lazy(() => import('./features/CADUpload/CADUploadContentPage'))
const createRequestSharepointPage = lazy(
  () => import('./pages/Sharepoint/CreateRequestSharepointPage')
)
const feedbackSubmissionDetailPage = lazy(
  () => import('./pages/Sharepoint/FeedbackSubmissionDetailPage')
)

function RoutesInner(): ReactElement {
  const history = useHistory()
  const log = new LogTool({ context: 'Router', enable: true, logLevel: 'debug' })
  const systemMode = useMediaQuery('(prefers-color-scheme: dark)')
  const [darkMode, setDarkMode] = useState<boolean>(systemMode)
  const [roles, setRoles] = useState<Role[]>([])
  const [userPermissions, setUserPermissions] = useState<NewPermissions>({})
  const [user, setUser] = useState<User | undefined>()
  const [accessToken, setAccessToken] = useState<string>(Cookies.get('access') || '')

  const [initialGuestRequests, setInitialGuestRequests] = useState<GuestRequest[]>([])
  const [initialGuestRequestsCount, setInitialGuestRequestsCount] = useState<number>(0)
  const [initialRequests, setInitialRequests] = useState<Request[]>([])
  const [initialRequestsCount, setInitialRequestsCount] = useState<number>(0)
  const [initialOffers, setInitialOffers] = useState<Offer[]>([])
  const [initialOffersCount, setInitialOffersCount] = useState<number>(0)
  const [initialContracts, setInitialContracts] = useState<Contract[]>([])
  const [initialContractsCount, setInitialContractsCount] = useState<number>(0)
  const [initialBills, setInitialBills] = useState<Bill[]>([])
  const [initialBillsCount, setInitialBillsCount] = useState<number>(0)
  const [countTotals, setCountTotals] = useState<number>(0)
  const [tabValue, setTabValue] = useState<number>(0)
  const [activeTabType, setActiveTabType] = useState<ContractTabType | null>(null)
  const [guestRequestQueryOptions, setGuestRequestQueryOptions] = useState<
    PaginatedQueryParameters<GuestRequest, GuestRequestFilters>
  >({
    expand: ['files', 'editors'],
    page: 1,
    pageSize: defaultTablePageSize,
  })
  const [requestQueryOptions, setRequestQueryOptions] = useState<
    PaginatedQueryParameters<Request, RequestFilters>
  >({
    expand: [
      'editors',
      'files',
      'article',
      'article__files',
      'article__properties',
      'article__category',
      'article__supplier',
      'contact',
    ],
    page: 1,
    pageSize: defaultTablePageSize,
  })
  const [offerQueryOptions, setOfferQueryOptions] = useState<
    PaginatedQueryParameters<Offer, OfferFilters>
  >({
    expand: ['editors', 'request', 'files'],
    page: 1,
    pageSize: defaultTablePageSize,
  })
  const [contractQueryOptions, setContractQueryOptions] = useState<
    PaginatedQueryParameters<Contract, ContractFilters>
  >({
    expand: ['offer', 'editors', 'shipping_address', 'billing_address', 'files'],
    page: 1,
    pageSize: defaultTablePageSize,
  })
  const [billQueryOptions, setBillQueryOptions] = useState<
    PaginatedQueryParameters<Bill, BillFilters>
  >({
    expand: ['contract', 'editors', 'files'],
    page: 1,
    pageSize: defaultTablePageSize,
  })
  const redirectTimerRef = useRef<NodeJS.Timeout | null>(null)
  /* const algorithmTheme = darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm */
  const muiTheme = MUItheme(/*darkMode*/)

  const searchParams = new URLSearchParams(window.location.search)
  const paramValue = searchParams.get('focus')
  log.debug('paramValue ->', paramValue)
  /* useEffect(() => {
    const mode = Cookies.get('darkMode')
    if (mode) {
      setDarkMode(mode === 'true')
    }
  }, []) */

  useEffect(() => {
    // Clear any pending redirect timer
    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current)
      redirectTimerRef.current = null
    }

    async function getNewToken() {
      const newToken: string | undefined = await refreshAccessToken()
      if (!newToken) {
        throw new Error('No new access token received')
      }
      Cookies.set('access', newToken)
      setAccessToken(newToken)
    }
    void (async () => {
      if (!accessToken) {
        const currentPath = window.location.pathname
        const isSignUpPage = currentPath === '/signUp'
        const isPasswordResetPage =
          currentPath === '/reset-password' || currentPath.startsWith('/reset-password/')
        const isVerifyPage = currentPath.includes('/event/')
        const isGuestSignInPage = currentPath.includes('guest-signIn')
        const isGuestRequestPage =
          currentPath.includes('guestRequest') || currentPath.includes('testGuestRequest')
        const isEmbedPage = currentPath.startsWith('/embed/')
        const isMaintenanceInfoPage = currentPath.includes('maintenance')
        const is404Page = currentPath === '/404'
        const isSignInPage = currentPath === '/signIn'

        // Skip refresh attempts on guest-signIn to avoid backend errors when refresh tokens are stale
        if (isGuestSignInPage) {
          return
        }

        const refreshToken = Cookies.get('refresh')
        if (refreshToken) {
          try {
            await getNewToken()
          } catch (err) {
            log.error('refreshAccessToken failed -> clearing tokens', err)
            Cookies.remove('access')
            Cookies.remove('refresh')
          }
        } else {
          if (isSignUpPage) {
            history.push('/signUp')
          } else if (isPasswordResetPage) {
            // Allow both /reset-password and /reset-password/:token routes
            // Don't redirect, let React Router handle the route matching
          } else if (isVerifyPage) {
            // } else if (isMaintenanceInfoPage) {   --  uncomment this line once the MaintenanceInfoPage is production ready
          } else if (isGuestRequestPage) {
            // do nothing -> stay on page
          } else if (isEmbedPage) {
            // do nothing -> allow embed pages (e.g., CAD upload iframe) without authentication
          } else if (is404Page) {
            // do nothing -> allow 404 page to be shown even when unauthenticated
          } else if (isSignInPage) {
            // do nothing -> already on sign in page
          } else {
            // For unknown routes, delay redirect to allow React Router to process the route first
            // If React Router redirects to /404, the useEffect will run again and see /404 (which is allowed)
            // If the route doesn't match and we're still on the unknown path, redirect to signIn
            redirectTimerRef.current = setTimeout(() => {
              const stillOnUnknownPath = window.location.pathname === currentPath
              const isNow404Page = window.location.pathname === '/404'
              if (stillOnUnknownPath && !isNow404Page && window.location.pathname !== '/signIn') {
                history.push('/signIn')
              }
              redirectTimerRef.current = null
            }, 50)
          }
        }
      } else {
        try {
          const user = await fetchCurrentUser(['roles'])
          if (Array.isArray(user.roles)) {
            setRoles(user.roles)
            setUserPermissions(rolesToPermissions(user.roles))
          }

          // Fetch organization name for non-guest users
          let userWithOrgName = user
          if (!user.isGuestUser && user.organization) {
            try {
              const organization = await fetchOrganization(user.organization)
              userWithOrgName = { ...user, organizationName: organization.name }
            } catch (orgError) {
              log.warn('Failed to fetch organization name:', orgError)
              // Continue without organization name
            }
          }

          setUser(userWithOrgName)
          // Don't fetch unread counts for guest users - they don't have access to these resources
          if (!user.isGuestUser) {
            await countNewRequests()
          }
        } catch (error) {
          log.error('fetchUser ->', error)
        }
      }
    })()

    // Cleanup function
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
    }
  }, [accessToken, history])

  const countNewRequests = useCallback(async (): Promise<void> => {
    try {
      const overview = await fetchUnreadOverview()
      const guestRequests = overview.guestRequests ?? { items: [], count: 0 }
      const requests = overview.requests ?? { items: [], count: 0 }
      const offers = overview.offers ?? { items: [], count: 0 }
      const contracts = overview.contracts ?? { items: [], count: 0 }
      const bills = overview.bills ?? { items: [], count: 0 }

      unstable_batchedUpdates(() => {
        setInitialGuestRequests(guestRequests.items.slice(0, 25))
        setInitialGuestRequestsCount(guestRequests.count)

        setInitialRequests(requests.items.slice(0, 25))
        setInitialRequestsCount(requests.count)

        setInitialOffers(offers.items.slice(0, 25))
        setInitialOffersCount(offers.count)

        setInitialContracts(contracts.items.slice(0, 25))
        setInitialContractsCount(contracts.count)

        setInitialBills(bills.items.slice(0, 25))
        setInitialBillsCount(bills.count)

        setCountTotals(overview.total ?? 0)
      })
    } catch (error) {
      log.error('Error in countNewRequests:', error)
    }
  }, [])

  const MainLayoutWithSetUser = (props: any) => (
    <MainLayout {...props} setUser={setUser} darkMode={darkMode} setDarkMode={setDarkMode} />
  )

  log.debug('User ->', user, ', accessToken ->', accessToken)

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#16a9e4',
        },
        // algorithm: algorithmTheme,
      }}
    >
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        <Suspense fallback={<LoadingSkeleton pagetitle={''} />}>
          <JoyrideProvider>
            <UserContext.Provider
              value={{ accessToken, setAccessToken, user, setUser, roles, userPermissions }}
            >
              <CountContext.Provider
                value={{
                  initialGuestRequests,
                  setInitialGuestRequests,
                  initialGuestRequestsCount,
                  setInitialGuestRequestsCount,
                  initialRequests,
                  setInitialRequests,
                  initialRequestsCount,
                  setInitialRequestsCount,
                  initialOffers,
                  setInitialOffers,
                  initialOffersCount,
                  setInitialOffersCount,
                  initialContracts,
                  setInitialContracts,
                  initialContractsCount,
                  setInitialContractsCount,
                  initialBills,
                  setInitialBills,
                  initialBillsCount,
                  setInitialBillsCount,
                  countTotals,
                  setCountTotals,
                  tabValue,
                  setTabValue,
                  activeTabType,
                  setActiveTabType,
                  guestRequestQueryOptions,
                  setGuestRequestQueryOptions,
                  requestQueryOptions,
                  setRequestQueryOptions,
                  offerQueryOptions,
                  setOfferQueryOptions,
                  contractQueryOptions,
                  setContractQueryOptions,
                  billQueryOptions,
                  setBillQueryOptions,
                }}
              >
                <ChatSystemGlobalStateProvider>
                  <SharepointGlobalStateProvider>
                    <WebsocketProvider>
                      <UnauthWebsocketProvider>
                        <UnreadCountsListener
                          enabled={
                            Boolean(user?.isStaff || user?.isSuperuser || user?.isOrgAdmin) &&
                            !user?.isGuestUser
                          }
                          onRefresh={countNewRequests}
                        />
                        <Switch>
                          <PublicRoute
                            path="/"
                            exact
                            layout={EmptyLayout}
                            component={() => (
                              <Redirect to={Cookies.get('refresh') ? '/items' : '/signIn'} />
                            )}
                          />

                          {isFeatureEnabled('guestRequests') && (
                            <PublicRoute
                              path="/guestRequest"
                              exact
                              layout={EmptyLayout}
                              component={guestRequestPage}
                            />
                          )}
                          {/* Embeddable CAD upload page for WordPress iframe */}
                          <PublicRoute
                            path="/embed/cad-upload"
                            exact
                            layout={EmptyLayout}
                            component={cadUploadPage}
                          />
                          {/* Embeddable CAD upload content only (without header/footer) */}
                          <PublicRoute
                            path="/embed/cad-upload/content"
                            exact
                            layout={EmptyLayout}
                            component={cadUploadContentPage}
                          />
                          {/* TODO: check if testGuestRequest Page is still needed */}
                          {/*{isFeatureEnabled('guestRequests') && (
                            <PublicRoute
                              path="/testGuestRequest"
                              exact
                              layout={EmptyLayout}
                              component={testGuestRequestPage}
                            />
                          )}*/}

                          {isFeatureEnabled('logistics') && (
                            <AuthenticatedRoute
                              path="/stockChangeDiagram"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={stockChangeDiagramPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('customer') && (
                            <AuthenticatedRoute
                              path="/contact/supplier/productionOrder"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={productionOrderPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('sharepoint') && (
                            <AuthenticatedRoute
                              path="/items/sharepoints"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={sharepointOverviewPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('logistics') && (
                            <AuthenticatedRoute
                              path="/items/itemstock"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={itemStockPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('supplier') && (
                            <AuthenticatedRoute
                              path="/contacts/supplier/orders"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={ordersPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('files') && (
                            <AuthenticatedRoute
                              path="/files"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={fileSystemPage}
                              user={user}
                            />
                          )}
                          {isFeatureEnabled('chat') && (
                            <AuthenticatedRoute
                              path="/chats"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={chatSystemPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}

                          {isFeatureEnabled('bom') && (
                            <AuthenticatedRoute
                              path="/items/bom/overview"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={bOMListPage}
                              user={user}
                            />
                          )}
                          {isFeatureEnabled('bom') && (
                            <AuthenticatedRoute
                              path="/items/bom/detail"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={bOMDetailPage}
                              user={user}
                            />
                          )}
                          {isFeatureEnabled('sharepoint') && (
                            <AuthenticatedRoute
                              path="/sharepoint/feedback-submissions/detail"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={feedbackSubmissionDetailPage}
                              user={user}
                            />
                          )}

                          {isFeatureEnabled('profile') && (
                            <AuthenticatedRoute
                              path="/profile"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={profilePage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('items') && (
                            <AuthenticatedRoute
                              path="/items"
                              component={itemsPage}
                              layout={MainLayoutWithSetUser}
                              user={user}
                            />
                          )}
                          {isFeatureEnabled('items') && (
                            <AuthenticatedRoute
                              path="/items/landingpage"
                              exact
                              layout={MainLayout}
                              component={mainLayoutWithSetUser}
                              user={user}
                            />
                          )}

                          {isFeatureEnabled('contacts') && (
                            <AuthenticatedRoute
                              path="/contacts"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={contactPage}
                              user={user}
                            />
                          )}
                          {isFeatureEnabled('contacts') && (
                            <AuthenticatedRoute
                              path="/contact_groups"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={contactGroupPage}
                              user={user}
                            />
                          )}
                          {isFeatureEnabled('customer') && (
                            <AuthenticatedRoute
                              path="/contacts/customer/list"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={customerPage}
                              user={user}
                            />
                          )}
                          {isFeatureEnabled('customer') && (
                            <AuthenticatedRoute
                              path="/contacts/customer/orders"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={customerOrdersPage}
                              user={user}
                            />
                          )}

                          {isFeatureEnabled('personnel') && (
                            <AuthenticatedRoute
                              path="/personnel/employees/list"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={employeePage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('personnel') && (
                            <AuthenticatedRoute
                              path="/personnel/organization"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={organizationPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('roles') && (
                            <AuthenticatedRoute
                              path="/personnel/roles/list"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={rolesPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('supplier') && (
                            <AuthenticatedRoute
                              path="/contacts/supplier/list"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={supplierPage}
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}
                          {isFeatureEnabled('support') && (
                            <AuthenticatedRoute
                              path="/support/contact"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={supportPage}
                              user={user}
                            />
                          )}
                          {(isFeatureEnabled('contracts') || isFeatureEnabled('guestRequests')) && (
                            <AuthenticatedRoute
                              path="/contracts"
                              exact
                              component={contractOverviewAdminPage}
                              layout={MainLayoutWithSetUser}
                              showBack
                              user={user}
                            />
                          )}
                          {/* TODO: Check if this page is needed */}
                          {/*{isFeatureEnabled('contracts') && (
                            <AuthenticatedRoute
                              path="/contracts/contracts/:id"
                              exact
                              component={contractSupplierSelectPage}
                              layout={MainLayoutWithSetUser}
                              showBack
                              title="pages.requestPage"
                              user={user}
                              guestUserRedirect="/items"
                            />
                          )}*/}

                          <PublicRoute
                            path="/404"
                            exact
                            layout={EmptyLayout}
                            component={WrongRoutePage}
                            title="404"
                          />

                          {isFeatureEnabled('auth') && (
                            <PublicRoute
                              path="/signIn"
                              exact
                              layout={AuthLayout}
                              component={() => <SignInPage paramValues={paramValue as string} />}
                            />
                          )}
                          {isFeatureEnabled('auth') && (
                            <PublicRoute
                              path="/signUp"
                              exact
                              layout={AuthLayout}
                              component={() => <SignUpPage setUser={setUser} />}
                            />
                          )}
                          {isFeatureEnabled('auth') && (
                            <PublicRoute
                              path="/event/:event"
                              exact
                              layout={AuthLayout}
                              component={VerifyPage}
                            />
                          )}
                          {isFeatureEnabled('auth') && (
                            <PublicRoute
                              path="/reset-password"
                              exact
                              layout={AuthLayout}
                              component={PasswordResetPage}
                            />
                          )}
                          {isFeatureEnabled('auth') && (
                            <PublicRoute
                              path="/reset-password/:token"
                              exact
                              layout={AuthLayout}
                              component={ChangePasswordPage}
                            />
                          )}
                          {isFeatureEnabled('auth') && (
                            <PublicRoute
                              path="/check-email"
                              exact
                              layout={AuthLayout}
                              component={CheckEmailPage}
                            />
                          )}
                          {isFeatureEnabled('guestRequests') && (
                            <PublicRoute
                              path="/send-request"
                              exact
                              layout={AuthLayout}
                              component={UnauthRequestPage}
                            />
                          )}
                          {isFeatureEnabled('sharepoint') && (
                            <PublicRoute
                              path="/guest-signIn/:token?"
                              exact
                              layout={AuthLayout}
                              component={GuestSignInPage}
                            />
                          )}
                          {isFeatureEnabled('sharepoint') && (
                            <PublicRoute
                              path="/request/create-sharepoint"
                              exact
                              layout={MainLayoutWithSetUser}
                              component={createRequestSharepointPage}
                            />
                          )}
                          <Redirect to="/404" />
                        </Switch>
                      </UnauthWebsocketProvider>
                    </WebsocketProvider>
                  </SharepointGlobalStateProvider>
                </ChatSystemGlobalStateProvider>
              </CountContext.Provider>
            </UserContext.Provider>
          </JoyrideProvider>
        </Suspense>
      </ThemeProvider>
    </ConfigProvider>
  )
}

function UnreadCountsListener({
  enabled,
  onRefresh,
}: {
  enabled: boolean
  onRefresh: () => Promise<void> | void
}): ReactElement | null {
  const { websocketMessage } = useWebsocket()
  const { user } = useUserContext()

  useEffect(() => {
    if (!enabled) return
    if (!websocketMessage) return
    if (websocketMessage.type !== 'count_overview_refresh') return

    // Don't refresh if the current user triggered this change
    // They already updated their local state
    // Also don't refresh if another user marked something as read, because
    // the counts are user-specific and won't change for the current user
    const messageUserId = websocketMessage.data?.user_id
    const currentUserId = user?.id
    const reason = websocketMessage.data?.reason

    if (messageUserId && currentUserId) {
      if (messageUserId === currentUserId) {
        // This user triggered the change, skip the refresh
        // They already updated their local state
        return
      } else if (reason && reason.endsWith('_marked_read')) {
        // Another user marked something as read
        // The counts are user-specific, so they won't change for the current user
        // Skip the refresh to avoid unnecessary table reloads
        return
      }
    }

    // Only refresh if we don't know who triggered the change or if it's not a mark-read event
    // This handles other types of count_overview_refresh messages
    void onRefresh()
  }, [enabled, onRefresh, websocketMessage, user])

  return null
}

export default function Routes(): ReactElement {
  return (
    <BrowserRouter>
      <RoutesInner />
    </BrowserRouter>
  )
}
